// React
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { NumericFormat } from "react-number-format";

// MUI
import {
    Box,
    TextField,
    Grid,
    Divider,
    Card,
    CardHeader,
    CardContent,
    Button,
    FormControl,
    InputLabel,
    OutlinedInput,
    InputAdornment,
    Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

const PREFIX = "Onboarding";

const classes = {
    btnRoot: `${PREFIX}-btnRoot`,
    buttonContainer: `${PREFIX}-buttonContainer`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.btnRoot}`]: {
        border: "1px solid #ff5968",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "#2f3190",
            border: "1px solid #2f3190",
        },
    },
    [`& .${classes.buttonContainer}`]: {
        display: "flex",
        justifyContent: "flex-end",
        padding: 15,
    },
}));

const NumberFormatCustom = (props) => {
    const todos = useSelector((state) => state.todos);

    const { inputRef, value, onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            value={value}
            getInputRef={inputRef}
            onValueChange={({ value: v }) => onChange({ target: { value: v } })}
            thousandSeparator={todos.amountFormat === "de-DE" ? "." : ","}
            decimalSeparator={todos.amountFormat === "de-DE" ? "," : "."}
            fixedDecimalScale={true}
            decimalScale={2}
            valueIsNumericString
        />
    );
};

const defaultState = {
    initDate: null,
    meetingDate: null,
    fee: 0,
    discount: 0,
    totalFee: 0,
    description: "",
};

export const Onboarding = (props) => {
    // Parámetros
    const {
        setTexto,
        setalertType,
        setShow,
        companyID,
        setBackDrop,
        data,
        reloadProjects,
        serviceID,
        controlSystem,
        expenseEst,
    } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component states */
    const [valuesOnboard, setOnboard] = useState(defaultState);
    const [isError, setIsError] = useState(false);

    /** Component functions */
    useEffect(() => {
        setOnboard({
            billed: data.billed,
            initDate: data.since_date ? moment(data.since_date).utc() : moment().utc(),
            meetingDate: data.end_date ? moment(data.end_date).utc() : null,
            fee: data.fee,
            discount: data.discount ?? 0,
            totalFee: data.fee * (1 - (data.discount ?? 0) / 100),
            description: data.description ?? "",
        });
    }, [data]);

    const handleChange = (event) => {
        setOnboard({
            ...valuesOnboard,
            [event.target.name]: event.target.value,
        });
    };

    const handleChangeDiscount = (event) => {
        let discountAux =
            event.target.value >= 0 && event.target.value <= 100 ? event.target.value : valuesOnboard.discount;
        setOnboard({
            ...valuesOnboard,
            discount: discountAux,
            totalFee: valuesOnboard.fee * (1 - discountAux / 100),
        });
    };

    const updateProject = async () => {
        setBackDrop(true);

        if (valuesOnboard.meetingDate) {
            let params = {
                company_id: companyID,
                service_id: serviceID,
                project_id: data.project_id,
                project_company_id: data.project_company_id,
                total_fee: valuesOnboard.totalFee,
                since_date: valuesOnboard.initDate.startOf("day").format(),
                end_date: valuesOnboard.meetingDate.startOf("day").format(),
                discount: valuesOnboard.discount ? parseFloat(valuesOnboard.discount) : 0,
                fee: valuesOnboard.fee,
                description: valuesOnboard.description,
            };

            await fetch(`/addServicesAndProjects`, {
                method: "POST",
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                    "Access-Control-Allow-Origin": "*",
                },
                body: JSON.stringify(params),
            })
                .then((response) => {
                    if (response.ok) {
                        setTexto(t("miscellaneous.successfulUpdate"));
                        setalertType("success");
                        setShow(true);
                        reloadProjects(true);
                    } else {
                        setTexto(t("miscellaneous.updatingError"));
                        setalertType("error");
                        setShow(true);
                    }
                })
                .catch((error) => {
                    console.log(error);
                });
        } else {
            setTexto(t("miscellaneous.requiredData"));
            setalertType("warning");
            setShow(true);
            setIsError(true);
        }

        setBackDrop(false);
    };

    const handleChangeMeetingDate = (date) => {
        if (!date || !valuesOnboard.initDate) {
            setOnboard({
                ...valuesOnboard,
                meetingDate: date,
            });
        } else {
            if (date.isBefore(valuesOnboard.initDate.format("YYYY-MM-DD"))) {
                setOnboard({
                    ...valuesOnboard,
                    meetingDate: null,
                });

                setTexto(t("reports.meetingDateMess"));
                setalertType("warning");
                setShow(true);
            } else {
                setOnboard({
                    ...valuesOnboard,
                    meetingDate: date,
                });
            }
        }
    };

    return (
        <StyledContainer>
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={t("language.locale")}
                localeText={{ clearButtonLabel: t("miscellaneous.clear") }}
            >
                <Card elevation={0}>
                    <CardHeader title="Controller onboarding" />
                    <Divider />
                    <CardContent>
                        {controlSystem.disabledData === true &&
                            (controlSystem.prov === "Xero" || controlSystem.prov === "Quickbooks Online") && (
                                <Grid container spacing={2} justifyContent="center" sx={{ paddingBottom: 2 }}>
                                    <Grid item>
                                        <Alert
                                            severity="warning"
                                            sx={{
                                                borderRadius: 8,
                                                fontSize: 12,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {t("services.enterValidCredentials")}
                                        </Alert>
                                    </Grid>
                                </Grid>
                            )}
                        {expenseEst === -1 && (
                            <Grid container spacing={2} justifyContent="center" sx={{ paddingBottom: 4 }}>
                                <Grid item>
                                    <Alert
                                        severity="warning"
                                        sx={{
                                            borderRadius: 8,
                                            fontSize: 12,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {t("services.ableToSaveInfo")}
                                    </Alert>
                                </Grid>
                            </Grid>
                        )}
                        <Grid container alignItems="flex-start" spacing={2}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <DatePicker
                                    disabled
                                    value={valuesOnboard.initDate}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => <TextField fullWidth required {...params} />}
                                    label={t("reports.initDate")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormControl fullWidth variant="outlined" disabled>
                                    <InputLabel htmlFor="outlined-adornment-fee">{t("services.projectFee")}</InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-fee"
                                        value={valuesOnboard.fee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("services.projectFee")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <TextField
                                    fullWidth
                                    label={t("miscellaneous.discount")}
                                    value={valuesOnboard.discount}
                                    disabled={
                                        valuesOnboard.billed ||
                                        (controlSystem.disabledData === true &&
                                            (controlSystem.prov === "Xero" ||
                                                controlSystem.prov === "Quickbooks Online")) ||
                                        expenseEst === -1
                                    }
                                    name="discount"
                                    onChange={handleChangeDiscount}
                                    onBlur={() =>
                                        setOnboard({
                                            ...valuesOnboard,
                                            discount: parseFloat(
                                                valuesOnboard.discount === "" ? 0 : valuesOnboard.discount
                                            ).toFixed(2),
                                        })
                                    }
                                    variant="outlined"
                                    type="number"
                                    InputProps={{
                                        inputProps: {
                                            min: 0,
                                            max: 100,
                                        },
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                    sx={{
                                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                            display: "none",
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    disabled
                                >
                                    <InputLabel htmlFor="outlined-adornment-totalFee">
                                        {t("proposals.feeTotal")}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-totalFee"
                                        value={valuesOnboard.totalFee}
                                        inputComponent={NumberFormatCustom}
                                        startAdornment={
                                            <InputAdornment disableTypography position="start">
                                                $
                                            </InputAdornment>
                                        }
                                        label={t("proposals.feeTotal")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <DatePicker
                                    componentsProps={{
                                        actionBar: { actions: ["clear"] },
                                    }}
                                    value={valuesOnboard.meetingDate}
                                    onChange={(newValue) => handleChangeMeetingDate(newValue)}
                                    inputFormat={todos.dateFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            fullWidth
                                            required
                                            {...params}
                                            error={isError && !valuesOnboard.meetingDate}
                                        />
                                    )}
                                    inputProps={{ readOnly: true }}
                                    label={t("services.meetingDate")}
                                    disabled={
                                        (controlSystem.disabledData === true &&
                                            (controlSystem.prov === "Xero" ||
                                                controlSystem.prov === "Quickbooks Online")) ||
                                        expenseEst === -1
                                    }
                                    views={["year", "month", "day"]}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={6}>
                                <TextField
                                    fullWidth
                                    label={t("miscellaneous.comments")}
                                    value={valuesOnboard.description}
                                    name="description"
                                    onChange={handleChange}
                                    variant="outlined"
                                    inputProps={{ maxLength: 100 }}
                                    disabled={
                                        (controlSystem.disabledData === true &&
                                            (controlSystem.prov === "Xero" ||
                                                controlSystem.prov === "Quickbooks Online")) ||
                                        expenseEst === -1
                                    }
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                    <Divider />
                    <Box className={classes.buttonContainer}>
                        <Button
                            color="secondary"
                            variant="contained"
                            disableElevation
                            classes={{ root: classes.btnRoot }}
                            onClick={updateProject}
                            disabled={
                                (controlSystem.disabledData === true &&
                                    (controlSystem.prov === "Xero" || controlSystem.prov === "Quickbooks Online")) ||
                                expenseEst === -1
                            }
                        >
                            {t("miscellaneous.save")}
                        </Button>
                    </Box>
                </Card>
            </LocalizationProvider>
        </StyledContainer>
    );
};
